/**
 * OpenAI Provider Implementation
 * Integrates with OpenAI's API using Vercel AI SDK
 */

import { createOpenAI } from '@ai-sdk/openai';
import { generateText, generateObject, streamText } from 'ai';
import { z } from 'zod';
import {
  AIProvider,
  AIModel,
  GenerateTextParams,
  GenerateObjectParams,
  StreamTextParams,
  TextResponse,
  ObjectResponse,
  TextChunk,
  Message,
  ChatOptions,
  ChatResponse,
  ToolRequest,
  ToolResponse,
  AIError,
  AIErrorType,
} from '../types';
import { configManager } from '../config';

/**
 * OpenAI model definitions
 */
const OPENAI_MODELS: AIModel[] = [
  {
    id: 'gpt-4o',
    name: 'GPT-4o',
    provider: 'openai',
    capabilities: {
      chat: true,
      completion: true,
      tools: true,
      vision: true,
      streaming: true,
      maxTokens: 4096,
      contextWindow: 128000,
    },
    costPer1kTokens: {
      input: 0.0025,
      output: 0.01,
    },
  },
  {
    id: 'gpt-4o-mini',
    name: 'GPT-4o Mini',
    provider: 'openai',
    capabilities: {
      chat: true,
      completion: true,
      tools: true,
      vision: true,
      streaming: true,
      maxTokens: 16384,
      contextWindow: 128000,
    },
    costPer1kTokens: {
      input: 0.00015,
      output: 0.0006,
    },
  },
  {
    id: 'gpt-4-turbo',
    name: 'GPT-4 Turbo',
    provider: 'openai',
    capabilities: {
      chat: true,
      completion: true,
      tools: true,
      vision: true,
      streaming: true,
      maxTokens: 4096,
      contextWindow: 128000,
    },
    costPer1kTokens: {
      input: 0.01,
      output: 0.03,
    },
  },
  {
    id: 'gpt-3.5-turbo',
    name: 'GPT-3.5 Turbo',
    provider: 'openai',
    capabilities: {
      chat: true,
      completion: true,
      tools: true,
      vision: false,
      streaming: true,
      maxTokens: 4096,
      contextWindow: 16385,
    },
    costPer1kTokens: {
      input: 0.0005,
      output: 0.0015,
    },
  },
];

export class OpenAIProvider implements AIProvider {
  readonly id = 'openai';
  readonly name = 'OpenAI';
  readonly models = OPENAI_MODELS;
  
  private createProvider(apiKey: string) {
    return createOpenAI({ apiKey });
  }

  private getApiKey(): string {
    const config = configManager.getProviderConfig('openai');
    if (!config?.apiKey) {
      throw new AIError(
        AIErrorType.API_KEY_INVALID,
        'OpenAI API key not configured',
        this.id
      );
    }
    return config.apiKey;
  }

  private getModel(modelId?: string): string {
    const config = configManager.getProviderConfig('openai');
    return modelId || config?.defaultModel || 'gpt-4o-mini';
  }

  private convertMessages(messages: Message[]): any[] {
    return messages.map(msg => ({
      role: msg.role,
      content: msg.content,
      ...(msg.name && { name: msg.name }),
      ...(msg.tool_calls && { tool_calls: msg.tool_calls }),
    }));
  }

  async generateText(params: GenerateTextParams): Promise<TextResponse> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(params.model);
      
      const openaiProvider = this.createProvider(apiKey);

      const result = await generateText({
        model: openaiProvider(modelId),
        prompt: params.prompt,
        ...(params.systemPrompt && { system: params.systemPrompt }),
        ...(params.temperature !== undefined && { temperature: params.temperature }),
        ...(params.maxTokens && { maxTokens: params.maxTokens }),
        ...(params.stopSequences && { stop: params.stopSequences }),
      });

      return {
        text: result.text,
        model: modelId,
        usage: result.usage ? {
          promptTokens: result.usage.promptTokens,
          completionTokens: result.usage.completionTokens,
          totalTokens: result.usage.totalTokens,
        } : undefined,
        finishReason: result.finishReason as any,
      };
    } catch (error) {
      throw this.handleError(error, params.model);
    }
  }

  async generateObject<T>(params: GenerateObjectParams<T>): Promise<ObjectResponse<T>> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(params.model);

      // Convert JSON Schema to Zod schema (simplified - you may need a proper converter)
      const schema = z.object(params.schema as any);

      const result = await generateObject({
        model: this.createProvider(apiKey)(modelId),
        prompt: params.prompt,
        schema,
        ...(params.systemPrompt && { system: params.systemPrompt }),
        ...(params.temperature !== undefined && { temperature: params.temperature }),
        ...(params.maxTokens && { maxTokens: params.maxTokens }),
      });

      return {
        object: result.object as T,
        model: modelId,
        usage: result.usage ? {
          promptTokens: result.usage.promptTokens,
          completionTokens: result.usage.completionTokens,
          totalTokens: result.usage.totalTokens,
        } : undefined,
      };
    } catch (error) {
      throw this.handleError(error, params.model);
    }
  }

  async *streamText(params: StreamTextParams): AsyncIterableIterator<TextChunk> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(params.model);

      const result = await streamText({
        model: this.createProvider(apiKey)(modelId),
        prompt: params.prompt,
        ...(params.systemPrompt && { system: params.systemPrompt }),
        ...(params.temperature !== undefined && { temperature: params.temperature }),
        ...(params.maxTokens && { maxTokens: params.maxTokens }),
        ...(params.stopSequences && { stop: params.stopSequences }),
      });

      let isFirst = true;
      for await (const chunk of result.textStream) {
        const textChunk: TextChunk = {
          text: chunk,
          isFirst,
          isLast: false,
        };
        isFirst = false;
        
        if (params.onChunk) {
          params.onChunk(textChunk);
        }
        
        yield textChunk;
      }

      // Final chunk
      yield { text: '', isFirst: false, isLast: true };
    } catch (error) {
      throw this.handleError(error, params.model);
    }
  }

  async chat(messages: Message[], options?: ChatOptions): Promise<ChatResponse> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(options?.model);
      const convertedMessages = this.convertMessages(messages);

      const result = await generateText({
        model: this.createProvider(apiKey)(modelId),
        messages: convertedMessages,
        ...(options?.systemPrompt && { system: options.systemPrompt }),
        ...(options?.temperature !== undefined && { temperature: options.temperature }),
        ...(options?.maxTokens && { maxTokens: options.maxTokens }),
        // TODO: Fix tool support for AI SDK v4
        // ...(options?.tools && { tools: options.tools }),
        // ...(options?.toolChoice && { toolChoice: options.toolChoice }),
      });

      return {
        message: {
          role: 'assistant',
          content: result.text,
          tool_calls: result.toolCalls as any,
        },
        model: modelId,
        usage: result.usage ? {
          promptTokens: result.usage.promptTokens,
          completionTokens: result.usage.completionTokens,
          totalTokens: result.usage.totalTokens,
        } : undefined,
        finishReason: result.finishReason as any,
      };
    } catch (error) {
      throw this.handleError(error, options?.model);
    }
  }

  async *streamChat(messages: Message[], options?: ChatOptions): AsyncIterableIterator<ChatResponse> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(options?.model);
      const convertedMessages = this.convertMessages(messages);

      const result = await streamText({
        model: this.createProvider(apiKey)(modelId),
        messages: convertedMessages,
        ...(options?.systemPrompt && { system: options.systemPrompt }),
        ...(options?.temperature !== undefined && { temperature: options.temperature }),
        ...(options?.maxTokens && { maxTokens: options.maxTokens }),
        // TODO: Fix tool support for AI SDK v4
        // ...(options?.tools && { tools: options.tools }),
        // ...(options?.toolChoice && { toolChoice: options.toolChoice }),
      });

      let accumulatedText = '';
      for await (const chunk of result.textStream) {
        accumulatedText += chunk;
        yield {
          message: {
            role: 'assistant',
            content: accumulatedText,
          },
          model: modelId,
        };
      }
    } catch (error) {
      throw this.handleError(error, options?.model);
    }
  }

  async executeTools(request: ToolRequest): Promise<ToolResponse> {
    try {
      const apiKey = this.getApiKey();
      const modelId = this.getModel(request.model);
      const convertedMessages = this.convertMessages(request.messages);

      const result = await generateText({
        model: this.createProvider(apiKey)(modelId),
        messages: convertedMessages,
        // TODO: Fix tool support for AI SDK v4
        // tools: request.tools,
        // toolChoice: request.toolChoice || 'auto',
      });

      return {
        toolCalls: result.toolCalls as any || [],
        message: {
          role: 'assistant',
          content: result.text,
          tool_calls: result.toolCalls as any,
        },
        model: modelId,
        usage: result.usage ? {
          promptTokens: result.usage.promptTokens,
          completionTokens: result.usage.completionTokens,
          totalTokens: result.usage.totalTokens,
        } : undefined,
      };
    } catch (error) {
      throw this.handleError(error, request.model);
    }
  }

  async validateApiKey(): Promise<boolean> {
    try {
      const apiKey = this.getApiKey();
      
      // Make a minimal API call to validate the key
      await generateText({
        model: this.createProvider(apiKey)('gpt-3.5-turbo'),
        prompt: 'Hi',
        maxTokens: 1,
      });
      
      return true;
    } catch (error) {
      return false;
    }
  }

  getModelInfo(modelId: string): AIModel | undefined {
    return this.models.find(m => m.id === modelId);
  }

  listModels(): AIModel[] {
    return [...this.models];
  }

  private handleError(error: any, modelId?: string): AIError {
    const errorMessage = error.message || 'Unknown error';
    
    if (errorMessage.includes('API key')) {
      return new AIError(
        AIErrorType.API_KEY_INVALID,
        'Invalid OpenAI API key',
        this.id,
        modelId,
        error
      );
    }
    
    if (errorMessage.includes('rate limit')) {
      return new AIError(
        AIErrorType.RATE_LIMIT_EXCEEDED,
        'OpenAI rate limit exceeded',
        this.id,
        modelId,
        error
      );
    }
    
    if (errorMessage.includes('context length') || errorMessage.includes('maximum')) {
      return new AIError(
        AIErrorType.CONTEXT_LENGTH_EXCEEDED,
        'Context length exceeded',
        this.id,
        modelId,
        error
      );
    }
    
    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return new AIError(
        AIErrorType.NETWORK_ERROR,
        'Network error connecting to OpenAI',
        this.id,
        modelId,
        error
      );
    }
    
    return new AIError(
      AIErrorType.INVALID_RESPONSE,
      `OpenAI error: ${errorMessage}`,
      this.id,
      modelId,
      error
    );
  }
}